import {
  AddAttachmentTicketEvent,
  UpdateTicketEvent,
  User,
} from '@certhon/models';
import { ReactNode } from 'react';
import { buildTicketUrl } from '@/lib/ticketUrls';
import { StackLink } from '@/app/navigation/StackLink';
import { ExtendedTicketEvent } from './Timeline/timeline-utils';

interface EventDescriptionProps {
  event: ExtendedTicketEvent;
  user?: User;
}

export function EventDescription({
  event,
  user,
}: EventDescriptionProps): ReactNode {
  const userName = user?.name || user?.loginname || `User ${event.user_id}`;
  switch (event.type) {
    case 'CREATE_TICKET': {
      return `${userName} created this ticket`;
    }

    case 'UPDATE_TICKET': {
      const payload = (event as UpdateTicketEvent).payload;
      const changes: string[] = [];

      if (payload.title !== undefined) changes.push('title');
      if (payload.description !== undefined) changes.push('description');
      // TODO: add statusbadge to the changes
      if (payload.status !== undefined) {
        changes.push(`status to ${payload.status}`);
      }
      if (payload.due !== undefined) {
        if (payload.due === null) {
          changes.push('removed due date');
        } else {
          changes.push(`due date to ${payload.due.toLocaleDateString()}`);
        }
      }
      if (payload.assignee_user_id !== undefined) {
        if (payload.assignee_user_id === null) {
          changes.push('removed assigned user');
        } else {
          changes.push(`assigned user to User ${payload.assignee_user_id}`);
        }
      }
      if (payload.executor_user_id !== undefined) {
        if (payload.executor_user_id === null) {
          changes.push('removed executor');
        } else {
          changes.push(`executor to User ${payload.executor_user_id}`);
        }
      }
      if (payload.project_id !== undefined) {
        changes.push(`project to ${payload.project_id}`);
      }
      if (payload.werk_id !== undefined) {
        changes.push(`work item to ${payload.werk_id}`);
      }
      // TODO: add labelbadges to the changes
      if (payload.add_labels !== undefined) {
        changes.push(`labels`);
      }
      if (payload.remove_labels !== undefined) {
        changes.push(`labels`);
      }

      if (changes.length === 0) {
        return `${userName} updated the ticket`;
      }

      if (changes.length === 1) {
        return `${userName} changed ${changes[0]}`;
      }

      const lastChange = changes.pop();
      return `${userName} changed ${changes.join(', ')} and ${lastChange}`;
    }

    case 'ADD_MESSAGE': {
      return `${userName} posted a message`;
    }

    case 'UPDATE_MESSAGE': {
      return `${userName} edited a message`;
    }

    case 'ADD_ATTACHMENT': {
      const payload = (event as AddAttachmentTicketEvent).payload;
      const filename = payload.filename || 'attachment';
      return `${userName} uploaded ${filename}`;
    }

    case 'ADD_LOCATION': {
      const label = event.payload.label;
      return label
        ? `${userName} added location "${label}"`
        : `${userName} added location`;
    }

    case 'UPDATE_LOCATION': {
      return `${userName} updated location`;
    }

    case 'NOTIFICATION_OPT_IN': {
      return `${userName} opted in to notifications`;
    }

    case 'NOTIFICATION_OPT_OUT': {
      return `${userName} opted out of notifications`;
    }

    case 'TICKET_REFERENCE': {
      const ticket = event.payload.ticket;
      const { title, number } = ticket;

      return (
        <div className="flex">
          <span className="flex-shrink-0">{userName} referenced in </span>
          <StackLink
            to={buildTicketUrl(ticket)}
            title={`#${number} ${title}`}
            className="ml-1 truncate"
          >
            <span className="font-semibold">#{number}</span> {title}
          </StackLink>
        </div>
      );
    }

    default:
      return `${userName} performed an action`;
  }
}
